/*
 * Specimen codes: ISO-YYYYMMDD-HHMM-LOC-NNN.
 *
 * The server mints them (mushroom-bank/lambda/routes/specimen_ids.py) and is
 * the only authority on the number. This file reads a code back into its
 * parts for display, recognises one when it is scanned, and states the
 * browser's own UTC offset -- which the server needs, because the code's date
 * and time are the collector's wall clock, not UTC.
 */

const CODE = /^ISO-(\d{4})(\d{2})(\d{2})-(\d{4})-([A-Z0-9]{2,8})-(\d{3,6})$/;

/** {date: 'YYYY-MM-DD', time: 'HH:MM', location, seq} or null. */
export function parseSpecimenCode(text) {
  const m = CODE.exec(String(text || '').trim().toUpperCase());
  if (!m) return null;
  const [, year, month, day, hhmm, location, seq] = m;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) return null;
  const hours = Number(hhmm.slice(0, 2));
  const minutes = Number(hhmm.slice(2));
  if (hours > 23 || minutes > 59 || Number(seq) < 1) return null;
  return {
    date: `${year}-${month}-${day}`,
    time: `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`,
    location,
    seq: Number(seq),
  };
}

export const isSpecimenCode = (text) => parseSpecimenCode(text) !== null;

/**
 * A collection's tag as it should be sent: trimmed, and a specimen code in
 * capitals. Plate, slant and DNA codes are built from the collection's tag
 * (routes/plates.py), so a code typed in lowercase on a phone would give
 * lowercase plate codes. Any other tag is the operator's own, and only trimmed.
 */
export function normaliseTag(value) {
  const text = String(value ?? '').trim();
  return isSpecimenCode(text) ? text.toUpperCase() : text;
}

/** Minutes east of UTC on this device, at `date` (summer time included). */
export const offsetMinutes = (date = new Date()) => -date.getTimezoneOffset();

const pad = (n) => String(n).padStart(2, '0');

/** `date` as ISO 8601 with this device's offset: 2026-10-12T09:30:00+03:00. */
export function localIsoWithOffset(date = new Date()) {
  const offset = offsetMinutes(date);
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    + `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * An <input type="datetime-local"> value ("2019-05-03T07:15") read on this
 * device's clock, with the offset that applied ON THAT DATE -- so a winter
 * collection entered in summer still gets winter time. A bare date
 * ("1998-04-12") is passed through: the server reads it as "time unknown".
 */
export function fromLocalInput(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : localIsoWithOffset(date);
}

/** A stored ISO moment as a datetime-local input value, on its own offset. */
export function toLocalInput(iso) {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(String(iso || ''));
  return m ? `${m[1]}T${m[2]}` : '';
}

/**
 * "2026-10-12 09:30" from a stored local ISO moment, or ''. A bare date
 * ("2026-10-12": the day only, as the server shows a student an isolate they
 * reach through an assignment) reads as that day.
 */
export function formatLocalMoment(iso, timeKnown = true) {
  const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(String(iso || ''));
  if (!m) return '';
  return timeKnown && m[2] ? `${m[1]} ${m[2]}` : m[1];
}
